/**
 * API Keys — authed page for managing org-scoped SDK credentials.
 * =================================================================
 * Lists existing keys (no secrets), generates a new key (plaintext
 * shown ONCE in a copy-now panel), and revokes keys. Pairs with the
 * /developers code-snippet docs.
 *
 * Brand: Slate-950 base, Emerald/Cyan accents, soft amber for warnings.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import {
  AlertTriangle, Copy, Check, KeyRound, Plus, Trash2, ShieldCheck,
  ExternalLink, Loader2, Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import BrandTitle from '../components/BrandTitle';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const KeyCreatedPanel = ({ created, onClose }) => {
  const [copiedKey, setCopiedKey] = useState(false);
  const [copiedSecret, setCopiedSecret] = useState(false);

  const copy = async (text, setter) => {
    try {
      await navigator.clipboard.writeText(text);
      setter(true);
      toast.success('Copied to clipboard');
      setTimeout(() => setter(false), 1800);
    } catch {
      toast.error('Copy failed — please select and copy manually');
    }
  };

  if (!created) return null;
  return (
    <Card className="bg-amber-500/10 border-amber-500/40 mb-6" data-testid="api-key-created-panel">
      <CardHeader>
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-amber-400" />
          <CardTitle className="text-amber-100 text-base">Save this key now — we can't show it again</CardTitle>
        </div>
        <CardDescription className="text-amber-200/80">
          We hash your key before storing it. Store it in your secret manager (1Password, AWS Secrets Manager, Doppler, etc.)
          right now. The webhook signing secret is used to verify outbound webhooks from CARTIE.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <p className="text-xs font-semibold text-amber-300 uppercase tracking-wider mb-1.5">API Key</p>
          <div className="flex items-center gap-2 bg-slate-950/60 border border-amber-500/30 rounded-md p-3 font-mono text-xs break-all text-amber-50">
            <code className="flex-1" data-testid="created-plaintext-key">{created.plaintext_key}</code>
            <Button size="sm" variant="ghost" className="text-amber-200 hover:text-white hover:bg-amber-500/20"
                    onClick={() => copy(created.plaintext_key, setCopiedKey)}
                    data-testid="copy-plaintext-key">
              {copiedKey ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
            </Button>
          </div>
        </div>
        <div>
          <p className="text-xs font-semibold text-amber-300 uppercase tracking-wider mb-1.5">Webhook Signing Secret</p>
          <div className="flex items-center gap-2 bg-slate-950/60 border border-amber-500/30 rounded-md p-3 font-mono text-xs break-all text-amber-50">
            <code className="flex-1" data-testid="created-signing-secret">{created.webhook_signing_secret}</code>
            <Button size="sm" variant="ghost" className="text-amber-200 hover:text-white hover:bg-amber-500/20"
                    onClick={() => copy(created.webhook_signing_secret, setCopiedSecret)}
                    data-testid="copy-signing-secret">
              {copiedSecret ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
            </Button>
          </div>
        </div>
        <div className="flex items-center justify-between pt-2">
          <Link to="/developers" className="text-sm text-emerald-300 hover:text-emerald-200 inline-flex items-center gap-1.5">
            See SDK code snippets <ExternalLink className="w-3.5 h-3.5" />
          </Link>
          <Button onClick={onClose} className="bg-emerald-500 hover:bg-emerald-400 text-slate-950" data-testid="dismiss-created-panel">
            <Check className="w-4 h-4 mr-1.5" /> I've saved them
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const ApiKeys = () => {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(null);

  const authHeaders = useCallback(() => {
    const tok = localStorage.getItem('token');
    return tok ? { Authorization: `Bearer ${tok}` } : {};
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api-keys`, { headers: authHeaders() });
      // Defensive dedupe — backend should always return unique ids, but
      // React's StrictMode double-invokes effects in dev, and an upstream
      // bug shouldn't crash the UI with duplicate-key warnings.
      const seen = new Set();
      const unique = (data.keys || []).filter((k) => {
        if (!k?.id || seen.has(k.id)) return false;
        seen.add(k.id);
        return true;
      });
      setKeys(unique);
    } catch (e) {
      toast.error('Could not load API keys — please refresh.');
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!name.trim()) { toast.error('Give the key a name (e.g. "Production server")'); return; }
    setCreating(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api-keys`,
        { name: name.trim(), scopes: ['events:write'] },
        { headers: authHeaders() },
      );
      setCreated(data);
      setCreateOpen(false);
      setName('');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create key.');
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (id, label) => {
    const ok = window.confirm(
      `Revoke "${label}"? Any service using this key will start getting 401s immediately. This cannot be undone.`
    );
    if (!ok) return;
    try {
      await axios.delete(`${API_URL}/api-keys/${id}`, { headers: authHeaders() });
      toast.success('Key revoked');
      await load();
    } catch (e) {
      toast.error('Could not revoke — please try again.');
    }
  };

  return (
    <div className="space-y-6 p-6 max-w-5xl mx-auto" data-testid="api-keys-page">
      <header className="flex items-start justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-400/30 mb-3">
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-300" />
            <span className="text-[11px] font-semibold tracking-widest text-emerald-200 uppercase">
              SDK Auth
            </span>
          </div>
          <h1 className="text-3xl font-bold text-white tracking-tight"><BrandTitle>API Keys</BrandTitle></h1>
          <p className="text-slate-400 max-w-2xl mt-1.5 text-sm">
            Generate org-scoped keys for SDK calls (event ingestion, webhook signing). Plaintext is shown once.
            We never see the key again — it's hashed before storage.
          </p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold" data-testid="new-key-btn">
              <Plus className="w-4 h-4 mr-1.5" /> New API key
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-slate-900 border-white/10 text-slate-100">
            <DialogHeader>
              <DialogTitle>New API Key</DialogTitle>
              <DialogDescription className="text-slate-400">
                Name it after where you'll use it ("Production server", "CI workflow", "Dev laptop")
                so you know which key to revoke later.
              </DialogDescription>
            </DialogHeader>
            <div className="py-2 space-y-3">
              <label className="text-xs font-medium text-slate-300">Key name</label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Production server"
                className="bg-slate-950 border-white/10 text-slate-100"
                data-testid="new-key-name-input"
                autoFocus
              />
              <p className="text-xs text-slate-400">
                Default scope: <code className="text-emerald-300">events:write</code>
                {' '}(required for sending AI cost events).
              </p>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setCreateOpen(false)} disabled={creating}>Cancel</Button>
              <Button onClick={handleCreate} disabled={creating}
                      className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold"
                      data-testid="confirm-create-key-btn">
                {creating ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Creating</>
                          : <><Sparkles className="w-4 h-4 mr-1.5" /> Generate key</>}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </header>

      <KeyCreatedPanel created={created} onClose={() => setCreated(null)} />

      <Card className="bg-slate-900/60 border-white/10">
        <CardHeader>
          <CardTitle className="text-base text-white flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-emerald-300" /> Active keys
          </CardTitle>
          <CardDescription className="text-slate-400">
            Keys are scoped to your organization. Revoking a key takes effect immediately.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-10 text-slate-400 text-sm">
              <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading…
            </div>
          ) : keys.length === 0 ? (
            <div className="py-12 text-center" data-testid="empty-keys">
              <KeyRound className="w-10 h-10 text-slate-400 mx-auto mb-3" />
              <p className="text-slate-300 font-medium mb-1">No API keys yet</p>
              <p className="text-slate-400 text-sm max-w-sm mx-auto">
                Generate a key to start sending real-time AI cost events from your servers.
                See <Link to="/developers" className="text-emerald-300 hover:text-emerald-200">developer docs</Link> for snippets.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-white/5" data-testid="api-keys-list">
              {keys.map((k) => (
                <div key={k.id} className="flex items-start justify-between py-3 gap-4" data-testid={`api-key-row-${k.id}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-white font-medium">{k.name}</span>
                      <Badge variant="outline" className="border-emerald-400/40 text-emerald-300 text-[11px]">
                        cartie_{k.prefix}…
                      </Badge>
                      {(k.scopes || []).map((s) => (
                        <Badge key={s} variant="outline" className="border-emerald-400/30 text-cyan-200 text-[11px]">{s}</Badge>
                      ))}
                      {k.revoked_at && (
                        <Badge className="bg-rose-500/15 text-rose-200 border-rose-500/30">Revoked</Badge>
                      )}
                    </div>
                    <p className="text-xs text-slate-400 mt-1">
                      Created {fmtDate(k.created_at)}{k.last_used_at ? ` · Last used ${fmtDate(k.last_used_at)}` : ' · Never used'}
                    </p>
                  </div>
                  {!k.revoked_at && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRevoke(k.id, k.name)}
                      className="text-rose-300 hover:text-rose-200 hover:bg-rose-500/10"
                      data-testid={`revoke-key-btn-${k.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Revoke
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-slate-900/40 border-white/10">
        <CardContent className="p-4 flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
          <div>
            <p className="text-white text-sm font-semibold">Need code snippets?</p>
            <p className="text-slate-400 text-xs">
              Python · JavaScript · cURL — plus webhook-signature verification with your signing secret.
            </p>
          </div>
          <Link to="/developers">
            <Button variant="outline" size="sm" className="border-emerald-400/40 text-emerald-200 hover:text-white hover:bg-emerald-500/10">
              Open developer docs <ExternalLink className="w-3.5 h-3.5 ml-1.5" />
            </Button>
          </Link>
        </CardContent>
      </Card>
    </div>
  );
};

export default ApiKeys;
